import React from 'react';

import { Box, styled, Typography } from '@mui/material';
import { Trans } from 'react-i18next';

import { CrossIcon } from '@setup/svg-icons/svg-icons';
import { MuiButton } from 'shared/ui-kit';

import type { TooltipRenderProps } from 'react-joyride';

export const TooltipTourContainer = styled(Box)(({ theme }) => ({
  borderRadius: '8px',
  boxShadow: '0px 15px 60px 0px rgba(38, 44, 74, 0.20)',
  padding: '16px',
  position: 'relative',
  maxWidth: '304px',
  minHeight: '104px',
  height: 'max-content',
  background: theme.palette.case.neutral.n0,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}));

type AdditionTooltipProps = {
  stepCount: number;
  customStartLabel?: string;
  customEndLabel?: string;
};
interface CustomTooltipRenderProps extends TooltipRenderProps {
  step:
    | ({
        title?: string;
        img?: string;
      } & TooltipRenderProps['step'])
    | any;
}

export const CustomTooltipTour =
  ({ stepCount, customStartLabel, customEndLabel }: AdditionTooltipProps) =>
  ({ index, isLastStep, primaryProps, skipProps, step, tooltipProps }: CustomTooltipRenderProps) => {
    const startLabel = customStartLabel || 'Got it!';
    const endLabel = customEndLabel || 'Next';
    return (
      <TooltipTourContainer {...tooltipProps}>
        {step.img && <img src={step.img} alt={'ss'} style={{ width: '100%', height: '200px' }} />}
        <Box
          {...skipProps}
          sx={{
            position: 'absolute',
            top: step.img || step.title ? 18 : 8,
            right: step.img || step.title ? 18 : 8,
            cursor: 'pointer',
            alignSelf: 'flex-start',
            svg: { width: '16px', height: '16px' },
          }}
        >
          <CrossIcon />
        </Box>
        {step.title && (
          <Box display="flex" justifyContent={'space-between'} width={'100%'}>
            <Typography variant="s1" color={(theme) => theme.palette.case.neutral.n900}>
              <Trans
                defaults={step.title as string}
                components={{
                  b: <Typography variant="t16r" component="span" fontWeight={700} />,
                }}
              />
            </Typography>
          </Box>
        )}
        <Box display="flex" mb={'8px'}>
          <Typography variant="t14r" color={(theme) => theme.palette.case.neutral.n700}>
            <Trans
              defaults={step.content as string}
              components={{
                b: <Typography variant="t16r" component="span" fontWeight={700} />,
              }}
            />
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {stepCount === 1 ? (
            <Box />
          ) : (
            <Typography variant="s3" color={(theme) => theme.palette.case.neutral.n500}>
              {index + 1} of {stepCount}
            </Typography>
          )}

          <MuiButton
            {...primaryProps}
            label={isLastStep ? startLabel : endLabel}
            variant="outlined"
            color="info"
            size="medium"
            disableFocusRipple
            disableRipple
          />
        </Box>
      </TooltipTourContainer>
    );
  };
